import type { ElementRef, FormEvent, JSX } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Alert, Form, ProcessActivityContent, ProcessActivityHeader, ProcessActivityItem } from '@ksyos/design-system';
import { Map } from 'immutable';
import { castArray, cloneDeep, get, isEqual, kebabCase, set } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CustomControlData, DefinedCustomControlData, IProcessAction } from '@common/interfaces';
import type { PerformProcessActionResult } from '@common/interfaces/performProcessAction';
import type { NL } from '@common/translate/nlMain';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import type { IValidationResult, ValidationErrors } from '@frontend/utils';
import {
    CustomControlMode,
    ProcessActivityStatus,
    ServiceActivityType,
    ToastType,
    ValidationSchemaVersion,
} from '@common/enums';
import { isProcessActivityAssets, isS3Asset } from '@common/utils/assetsUtils';
import { formatString } from '@common/utils/formatString';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { createProcessActivityContextData } from '@common/utils/serviceContextUtils';
import {
    getCustomControlValidationSchemaContainer,
    getRegistryEntry,
} from '@common/workflow/serviceActivityRegistration';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import {
    findRegistryEntry,
    getInitialData,
} from '@frontend/components/pages/episodeCategory/customControls/customControl';
import { PatientEpisodeCategoryContext } from '@frontend/components/pages/episodeCategory/patientEpisodeCategoryContext';
import { EditForm } from '@frontend/formEngine/editForm/EditForm';
import { getHookData } from '@frontend/formEngine/editForm/hookUtils';
import { createProcess } from '@frontend/services/api/patientsApiService';
import { getProcessActivityAssets } from '@frontend/services/api/processActivitiesApiService';
import { getAvailableActions } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import {
    scrollToContainerItemOrTop,
    scrollToRefValidationError,
    showToast,
    updateValidationErrorsForMany,
    useDebounce,
    useProcessActivityContext,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { ProcessContext } from '../../processContext';
import { getCommonHeaderProps, getMergedActivityData } from '../processActivityUtil';
import { ProcessActivityEditActionButtonRow } from './ProcessActivityEditActionButtonRow';
import { ProcessActivityEditContextProvider } from './ProcessActivityEditContextProvider';

export function ProcessActivityEdit(): JSX.Element {
    const abortSignal = useAbortSignal();
    // The process context will not exist when this is the first activity in the process
    const processContext = useContext(ProcessContext);
    const { customControlCode, customControlVersion, patientData, processActivity, processData, serviceActivity } =
        useProcessActivityContext();
    const { patient, reload, setSaveStatus } = useDefinedContext(PatientEpisodeCategoryContext);
    const {
        activeModifyActivity,
        activeProcessAction,
        activeStartingPoint,
        deleteProcessActionDraft,
        isSavingAndReloading,
        saveModifiedCustomControlData,
        saveProcess,
        saveProcessAction,
        setActiveProcessAction,
        setActiveStartingPoint,
        setActiveModifyActivity,
    } = useDefinedContext(PatientEpisodeCategoryCarePathContext);
    const registryEntry = findRegistryEntry(customControlCode, customControlVersion);
    const commonRegistryEntry = getRegistryEntry(customControlCode, customControlVersion);
    const initialActivityData = useMemo(getActivityData, [processActivity]);

    const [activityData, setActivityData] = useState(initialActivityData);
    const [activityDataValidateKeys, setActivityDataValidateKeys] = useState<string[] | undefined>(undefined);
    const [activityErrors, setActivityErrors] = useState<ValidationErrors>(Map());
    const [alertText, setAlertText] = useState<string | undefined>();
    const [autoSavedActivityData, setAutoSavedActivityData] = useState<DefinedCustomControlData>(initialActivityData);
    const [hookData, setHookData] = useState<unknown>({});
    const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(serviceActivity.isAutoSaveEnabled);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [isUploadingAsset, setIsUploadingAsset] = useState(false);
    const [processActionForAutoSavedActivity, setProcessActionForAutoSavedActivity] = useState<IProcessAction>();
    const [scrollToValidationError, setScrollToValidationError] = useState<ValidationErrors>();

    const autoSavedActivityDataRef = useRef(autoSavedActivityData);
    const formRef = useRef<ElementRef<'form'>>(null);
    const debounce = useDebounce(2000, { maxWait: 20000 });
    const activeOrAutoSavedProcessAction = processActionForAutoSavedActivity ?? activeProcessAction;
    const autoSavedProcessActivityId = activeOrAutoSavedProcessAction?.activities?.find(
        (activity) => activity.id === serviceActivity.id
    )?.processActivityId;
    const processActivityId = processActivity?.id ?? autoSavedProcessActivityId;
    const isModifying = Boolean(activeModifyActivity);
    const hasElementToDisplay = Boolean(registryEntry?.EditElement) || Boolean(commonRegistryEntry.sections);
    const processActivityContextData = createProcessActivityContextData({
        customControlData: activityData,
        hookData,
        patientData,
        processData,
    });

    useEffect(() => {
        const enableAutoSave = isAutoSaveEnabled && !isModifying;
        if (enableAutoSave) {
            if (!processActivity?.id) {
                // This causes a draft to be created for a new activity
                initialAutoSave(initialActivityData);

                return () => {
                    // Reload to ensure that parent components know there is something in draft
                    reload();
                };
            } else {
                return () => {
                    // Something was already in draft, only update parents in case the draft changed
                    if (!isEqual(initialActivityData, autoSavedActivityDataRef.current)) {
                        reload();
                    }
                };
            }
        }
        scrollToContainerItemOrTop(processActivity?.id);
    }, []);

    /*
     * This effect ensures that the hooks are retrieved for a new process activity or when a draft is opened.
     */
    useEffect(() => {
        if (commonRegistryEntry.hooks) {
            getHookData({
                abortSignal,
                customControlCode,
                customControlVersion,
                hooks: commonRegistryEntry.hooks,
                patientId: patient.id,
                processActivityContextData,
            }).then(setHookData);
        }
    }, [patientData, processData]);

    /*
     * The `isAutoSaving` dependency is necessary to trigger this effect when there is a change in the activity data
     * by the underlying edit component after `initialAutoSave` is called.
     * For example, by prefilling activity data that overrides the initial data.
     */
    useEffect(() => {
        if (isAutoSaveEnabled && !isAutoSaving) {
            debounce(() => autoSave(activityData));
        }
    }, [activityData, isAutoSaving]);

    /*
     * This effect is necessary to update the ref of the data so that the correct values can be used during unmount.
     */
    useEffect(() => {
        autoSavedActivityDataRef.current = autoSavedActivityData;
    }, [autoSavedActivityData]);

    useEffect(() => {
        if (activityDataValidateKeys) {
            setActivityErrors((prevErrors) => {
                const schemaContainer = getCustomControlValidationSchemaContainer(
                    customControlCode,
                    ValidationSchemaVersion.final,
                    customControlVersion
                );

                return updateValidationErrorsForMany(
                    schemaContainer,
                    processActivityContextData,
                    prevErrors,
                    activityDataValidateKeys
                );
            });
            setActivityDataValidateKeys(undefined);
        }
    }, [activityDataValidateKeys, customControlCode, customControlVersion, processActivityContextData]);

    useEffect(() => {
        scrollToRefValidationError(formRef);
    }, [scrollToValidationError]);

    return (
        <ProcessActivityEditContextProvider
            data={activityData}
            errors={activityErrors}
            formRef={formRef}
            handleCancel={handleCancel}
            onChange={handleChange}
            processActivityContextData={processActivityContextData}
            processActivityId={processActivityId}
            setActivityData={setActivityData}
            setHookData={setHookData}
            setIsAutoSaveEnabled={setIsAutoSaveEnabled}
            setIsUploadingAsset={setIsUploadingAsset}
        >
            <ProcessActivityItem mode={CustomControlMode.edit}>
                <ProcessActivityHeader
                    {...getCommonHeaderProps(hasElementToDisplay, serviceActivity, processActivity)}
                />
                {hasElementToDisplay && (
                    <ProcessActivityContent>
                        <Form
                            formRef={formRef}
                            onSubmit={saveFinal}
                            submitOnEnter={false}
                        >
                            {registryEntry?.EditElement ? (
                                <registryEntry.EditElement />
                            ) : (
                                <EditForm sections={nonNullable(commonRegistryEntry.sections)} />
                            )}
                            {alertText && (
                                <Alert
                                    appearance="warning"
                                    hasIcon={true}
                                >
                                    {alertText}
                                </Alert>
                            )}
                            {!registryEntry?.hideEditActions && (
                                <ProcessActivityEditActionButtonRow
                                    getSubmitButton={registryEntry?.ButtonElement}
                                    isDisabled={isSavingAndReloading || isUploadingAsset || isAutoSaving}
                                    isModifying={isModifying}
                                    onModifyActivity={modifyActivity}
                                    onSaveDraft={saveDraft}
                                />
                            )}
                        </Form>
                    </ProcessActivityContent>
                )}
            </ProcessActivityItem>
        </ProcessActivityEditContextProvider>
    );

    function getActivityData(): DefinedCustomControlData {
        const initialData = getInitialData(customControlCode, customControlVersion);

        // TODO: CS-24587 Remove cast once `(Defined)CustomControlData` and `Record<string, unknown>` are compatible
        return { ...initialData, ...processActivity?.customControlData } as DefinedCustomControlData;
    }

    function handleChange<Data extends Record<string, unknown>>(
        changedData: Partial<Data>,
        validateKeys?: string | string[]
    ): void {
        setAlertText(undefined);
        setActivityData((prevActivityData) =>
            // Deep clone the previous data so that the original object is not mutated and deep merge with the changed data
            // TODO: CS-24587 Remove cast once `(Defined)CustomControlData` and `Record<string, unknown>` are compatible
            getMergedActivityData(prevActivityData, changedData as Partial<CustomControlData>)
        );
        setActivityDataValidateKeys(validateKeys ? castArray(validateKeys) : undefined);
    }

    function getValidationResult(
        validationSchemaVersion: ValidationSchemaVersion,
        data: DefinedCustomControlData
    ): IValidationResult<DefinedCustomControlData> {
        const schemaContainer: ISchemaContainer<DefinedCustomControlData> = getCustomControlValidationSchemaContainer(
            customControlCode,
            validationSchemaVersion,
            customControlVersion
        );
        const dataForValidation = createProcessActivityContextData({
            customControlData: data,
            hookData,
            patientData,
            processData,
        });

        return validate(schemaContainer, dataForValidation);
    }

    /*
     * This function returns valid auto save activity data. If there are any validation errors, the invalid keys are
     * set to the last valid (auto saved) data to ensure that only valid data is saved.
     */
    function getValidAutoSaveActivityData(data: DefinedCustomControlData): DefinedCustomControlData {
        // Auto save should always validate with the draft schema
        const validationSchemaVersion = ValidationSchemaVersion.draft;
        const { errors, hasErrors } = getValidationResult(validationSchemaVersion, data);
        if (!hasErrors) {
            return data;
        }

        // Deep clone so that the original data is not mutated when setting invalid keys to last valid (auto saved) data
        const autoSaveData = cloneDeep(data);
        errors.forEach((_value, key) => {
            if (key) {
                const value = get(autoSavedActivityData, key, null);
                set(autoSaveData, key, value);
            }
        });

        return autoSaveData;
    }

    async function saveProcessActivity({
        data,
        isAutoSave = false,
        processAction,
        status,
    }: {
        data: DefinedCustomControlData;
        isAutoSave?: boolean;
        processAction: IProcessAction;
        status: ProcessActivityStatus;
    }): Promise<PerformProcessActionResult> {
        return saveProcessAction(processAction.processId, {
            actionId: processAction.id,
            activityData: {
                [serviceActivity.id]: data,
            },
            isAutoSave,
            processWorkflowStepId: processAction.processWorkflowStepId,
            serviceWorkflowStepId: processAction.serviceWorkflowStep.id,
            status,
        });
    }

    async function save(
        validationSchemaVersion: ValidationSchemaVersion,
        status: ProcessActivityStatus,
        statusLabel: keyof NL['StatusLabelEnum']
    ): Promise<void> {
        // Cancel any pending auto save actions
        debounce.cancel();
        const {
            errors,
            hasErrors,
            item: validatedActivityData,
        } = getValidationResult(validationSchemaVersion, activityData);
        setActivityErrors(errors);
        if (hasErrors) {
            return setScrollToValidationError(errors);
        }

        datadogRum.addAction(`Save ${kebabCase(serviceActivity.customControlCode)} ${status}`);

        const savedProcessActivityIds: string[] = [];
        if (activeOrAutoSavedProcessAction) {
            const result = await saveProcessActivity({
                data: validatedActivityData,
                processAction: activeOrAutoSavedProcessAction,
                status,
            });
            savedProcessActivityIds.push(...result.savedProcessActivityIds);
        } else if (activeStartingPoint) {
            await saveProcess({
                activityData: {
                    [serviceActivity.id]: validatedActivityData,
                },
                serviceId: activeStartingPoint.service.id,
                serviceWorkflowStepId: activeStartingPoint.serviceWorkflowStep.id,
                startingPointId: activeStartingPoint.id,
                status,
            });
        } else {
            return;
        }

        let toastMessage: string;
        if (serviceActivity.serviceActivityType === ServiceActivityType.unknown) {
            toastMessage = getSelectedLanguage().StatusLabelEnum[statusLabel];
        } else {
            const toastStatus = status === ProcessActivityStatus.complete ? 'sentSuccess' : 'savedSuccess';
            toastMessage = formatString(getSelectedLanguage().toast[toastStatus], [
                getSelectedLanguage().serviceActivityType[serviceActivity.serviceActivityType],
            ]);
        }
        showToast(ToastType.success, toastMessage);

        if (processContext) {
            savedProcessActivityIds.forEach(processContext.expandProcessActivity);
        }
    }

    async function handleCancel(): Promise<void> {
        if (isAutoSaveEnabled && activeOrAutoSavedProcessAction) {
            if (activeProcessAction?.processWorkflowStepId) {
                // The activity already existed as draft when starting: revert to initial state
                await saveProcessActivity({
                    data: initialActivityData,
                    processAction: activeOrAutoSavedProcessAction,
                    status: ProcessActivityStatus.draft,
                });
            } else {
                // The activity did not exist before editing started and was created by auto-save: delete it.
                // This is also the case when the process and activity were newly created by `activeStartingPoint`.
                await deleteProcessActionDraft(activeOrAutoSavedProcessAction);
            }
        }

        setActiveModifyActivity(undefined);
        setActiveProcessAction(undefined);
        setActiveStartingPoint(undefined);
        scrollToContainerItemOrTop(processActivity?.id);
    }

    /*
     * This loads the relevant `processAction` for an activity that has been auto-saved after initial render.
     * After the activity has been auto-saved, the related `processAction` will contain a `processWorkflowStepId`,
     * which needs to be used so that subsequent (auto)saves are stored correctly. It should only be called for an
     * activity that has not been previously saved as draft. The reason that there is a local state for this new
     * `processAction` is so that re-rendering of this component is prevented and the relevant `activeStartingPoint`
     * or `activeProcessAction` is still known.
     */
    async function loadProcessActionForAutoSavedActivity(
        processId: string,
        serviceWorkflowStepId: string,
        savedProcessActivityId?: string
    ): Promise<void> {
        const availableActions = await getAvailableActions(processId, { includePaused: true }, abortSignal);
        const processAction = availableActions.find(
            (action) =>
                action.serviceWorkflowStep.id === serviceWorkflowStepId &&
                (!savedProcessActivityId || savedProcessActivityId == action.activities?.[0].processActivityId)
        );
        if (!processAction) {
            throw new Error('Expected to find process action for auto-saved activity');
        }
        setProcessActionForAutoSavedActivity(processAction);
    }

    async function initialAutoSave(data: DefinedCustomControlData): Promise<void> {
        if (!activeStartingPoint && !activeProcessAction) {
            return;
        }

        setIsAutoSaving(true);
        if (activeStartingPoint) {
            setSaveStatus('saving');
            const serviceWorkflowStepId = activeStartingPoint.serviceWorkflowStep.id;
            const { processId } = await createProcess(
                patient.id,
                {
                    activityData: {
                        [serviceActivity.id]: data,
                    },
                    serviceId: activeStartingPoint.service.id,
                    serviceWorkflowStepId,
                    startingPointId: activeStartingPoint.id,
                    status: ProcessActivityStatus.draft,
                },
                abortSignal
            );
            await loadProcessActionForAutoSavedActivity(processId, serviceWorkflowStepId);
            setSaveStatus('saved');
        } else if (activeProcessAction) {
            // TypeScript does not see that at this point `activeProcessAction` should be defined, thus the check above
            const autoSaveResult = await saveProcessActivity({
                data,
                isAutoSave: true,
                processAction: activeProcessAction,
                status: ProcessActivityStatus.draft,
            });
            await loadProcessActionForAutoSavedActivity(
                activeProcessAction.processId,
                activeProcessAction.serviceWorkflowStep.id,
                autoSaveResult.savedProcessActivityIds[0]
            );
        }

        setIsAutoSaving(false);
    }

    async function autoSave(data: DefinedCustomControlData): Promise<void> {
        if (
            !activeOrAutoSavedProcessAction ||
            isSavingAndReloading ||
            isAutoSaving ||
            isEqual(data, autoSavedActivityData)
        ) {
            return;
        }

        const autoSaveActivityData = getValidAutoSaveActivityData(data);
        if (isEqual(autoSaveActivityData, autoSavedActivityData)) {
            return;
        }

        setIsAutoSaving(true);
        await saveProcessActivity({
            data: autoSaveActivityData,
            isAutoSave: true,
            processAction: activeOrAutoSavedProcessAction,
            status: ProcessActivityStatus.draft,
        });
        setAutoSavedActivityData(autoSaveActivityData);
        await updateAssetsWithoutId(autoSaveActivityData);
        setIsAutoSaving(false);
    }

    async function saveDraft(): Promise<void> {
        await save(ValidationSchemaVersion.draft, ProcessActivityStatus.draft, 'saved');
    }

    async function saveFinal(event: FormEvent): Promise<void> {
        event.preventDefault();
        await save(ValidationSchemaVersion.final, ProcessActivityStatus.complete, 'sent');
    }

    async function modifyActivity(): Promise<void> {
        if (!processActivityId) {
            throw new Error('Expected process activity id to be defined');
        }

        const {
            errors,
            hasErrors,
            item: validatedActivityData,
        } = getValidationResult(ValidationSchemaVersion.final, activityData);
        setActivityErrors(errors);

        if (hasErrors) {
            return setScrollToValidationError(errors);
        }

        if (isEqual(initialActivityData, activityData)) {
            return setAlertText(getSelectedLanguage().processActivity.modifyNoChangesWarning);
        }

        await saveModifiedCustomControlData(processActivityId, validatedActivityData);
        showToast(ToastType.success, getSelectedLanguage().toast.modificationSuccess);

        if (processContext) {
            processContext.expandProcessActivity(processActivityId);
        }
    }

    /*
     * This function synchronizes the assets that have been created in the back-end with the activity data.
     * In other words, it converts the S3 storage files (assets without id) into Database assets.
     */
    async function updateAssetsWithoutId(autoSavedData: DefinedCustomControlData): Promise<void> {
        if (!isProcessActivityAssets(autoSavedData?.assets)) {
            return;
        }

        // S3 assets are the assets that have been uploaded to S3 storage, but not saved as Database assets yet
        const s3AssetIds = autoSavedData.assets.filter(isS3Asset).map((asset) => asset.s3Id);
        if (autoSavedProcessActivityId && s3AssetIds?.length) {
            const { items: processActivityAssets } = await getProcessActivityAssets(
                autoSavedProcessActivityId,
                abortSignal
            );

            setActivityData((prevActivityData) => {
                // This is mostly a type guard, we expect that the activity data always has assets at this point
                if (!isProcessActivityAssets(prevActivityData?.assets)) {
                    return prevActivityData;
                }

                // The additional assets may be added to the activity data while the auto save is in progress.
                // These assets are not added to the back-end yet (they're still S3 assets and not Database assets).
                const additionalAssetsWithoutId = prevActivityData.assets
                    .filter(isS3Asset)
                    .filter((asset) => !s3AssetIds.includes(asset.s3Id));

                return {
                    ...prevActivityData,
                    assets: [...processActivityAssets, ...additionalAssetsWithoutId],
                } as DefinedCustomControlData;
            });
        }
    }
}
